// src/services/cnpj-external/jarvisly/adapter.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import entitiesService from 'services/entitiesService';

// *******************************************************************************
// ADAPTER INTERFACE
// *******************************************************************************

/**
 * Jarvisly CNPJ Provider Adapter (Database)
 * Searches for entity in internal database by CNPJ
 *
 * Implements provider contract:
 * - searchCnpj(payload) → Promise<Object>
 * - normalizeRequestPayload(payload) → Object
 * - normalizeResponseBody(response) → Universal POJO
 */

/**
 * Searches CNPJ data in database
 * @param {Object} payload - Search parameters
 * @param {string} payload.cnpj - CNPJ number (with or without formatting)
 * @returns {Promise<Object>} Universal POJO with CNPJ data
 */
async function searchCnpj(payload) {
  const normalizedRequest = normalizeRequestPayload(payload);

  // Search entity by CNPJ in database
  const response = await entitiesService.getEntityByField(normalizedRequest.cnpj);

  // Return raw response - factory will call normalizeResponseBody
  return response;
}

/**
 * Normalizes incoming payload to database query format
 * @param {Object} payload - Raw payload
 * @returns {Object} Database-compatible payload
 */
function normalizeRequestPayload(payload) {
  const { cnpj } = payload;

  // Remove formatting from CNPJ (keep only numbers)
  const cleanCnpj = String(cnpj || '').replace(/\D/g, '');

  return {
    cnpj: cleanCnpj,
  };
}

/**
 * Normalizes database response to universal POJO
 * @param {Object} response - Database entity document or null
 * @returns {Object} Universal POJO
 *
 * Database response structure (entity document):
 * {
 *   _id: string,
 *   cnpj: string,
 *   name: string,
 *   fullName: string,
 *   birthdate: string (foundation date),
 *   email: string,
 *   phone: string,
 *   businessAreaId: string,
 *   ...otherFields
 * }
 *
 * Universal POJO structure for CNPJ:
 * {
 *   provider: string,
 *   success: boolean,
 *   apiWorked: boolean,
 *   document: Object | null,  // Full entity document
 *   cnpj: string,
 *   name: string,
 *   fullName: string,
 *   birthdate: string,
 *   email: string,
 *   phone: string,
 *   businessAreaId: string,
 *   error: string | null,
 *   source: string
 * }
 */
function normalizeResponseBody(response) {
  const entityDoc = response;

  // Entity not found in database
  if (!entityDoc) {
    return {
      provider: 'jarvisly',
      success: false,
      apiWorked: true, // Database worked, but entity not found
      document: null,
      cnpj: null,
      name: null,
      fullName: null,
      birthdate: null,
      email: null,
      phone: null,
      businessAreaId: null,
      error: 'CNPJ não encontrado no banco de dados',
      source: 'jarvisly',
    };
  }

  // Success - Entity found in database
  return {
    provider: 'jarvisly',
    success: true,
    apiWorked: true,
    document: entityDoc, // Include full document for form filling
    cnpj: entityDoc.cnpj || null,
    name: entityDoc.name || null,
    fullName: entityDoc.fullName || null,
    birthdate: entityDoc.birthdate || null,
    email: entityDoc.email || null,
    phone: entityDoc.phone || null,
    businessAreaId: entityDoc.businessAreaId || null,
    error: null,
    source: 'jarvisly',
  };
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  searchCnpj,
  normalizeRequestPayload,
  normalizeResponseBody,
};
